/**
 * Brand/Miller/Trader QPS (Quantity Purchase Scheme) Volume Ladder Pricing Utility.
 * If no QPS scheme is configured for a SKU (or discount is 0), no ladder is shown or applied.
 */

export interface VolumeTier {
  tierName: string;
  minQty: number;
  maxQty?: number;
  discountPercent: number;
  pricePaise: number;
  savingPerUnitPaise: number;
}

export interface QpsConfig {
  pricePaise: number;
  qpsTier1Qty?: number | null;
  qpsTier1DiscountPercent?: number | null;
  qpsTier2Qty?: number | null;
  qpsTier2DiscountPercent?: number | null;
}

/**
 * Returns true only if the brand/miller has provided an active QPS scheme with discount > 0.
 */
export function hasActiveQpsScheme(item?: QpsConfig | null): boolean {
  if (!item) return false;
  const t1 = (item.qpsTier1Qty ?? 0) > 0 && (item.qpsTier1DiscountPercent ?? 0) > 0;
  const t2 = (item.qpsTier2Qty ?? 0) > 0 && (item.qpsTier2DiscountPercent ?? 0) > 0;
  return t1 || t2;
}

/**
 * Derives volume tiers for a SKU based on its configured QPS scheme.
 * If nothing is mentioned, returns an empty array (NO LADDER SHOWN).
 */
export function getVolumeTiers(item: QpsConfig | number): VolumeTier[] {
  if (typeof item === 'number') {
    return [];
  }
  if (!item || !item.pricePaise || item.pricePaise <= 0 || !hasActiveQpsScheme(item)) {
    return [];
  }

  const basePricePaise = item.pricePaise;
  const t1Qty = item.qpsTier1Qty ?? 0;
  const t1Pct = item.qpsTier1DiscountPercent ?? 0;
  const t2Qty = item.qpsTier2Qty ?? 0;
  const t2Pct = item.qpsTier2DiscountPercent ?? 0;

  const tiers: VolumeTier[] = [];

  // Base Lot (before the lowest slab)
  const lowestSlabQty = t1Qty > 0 ? t1Qty : t2Qty;
  if (lowestSlabQty > 1) {
    tiers.push({
      tierName: 'Standard Lot',
      minQty: 1,
      maxQty: lowestSlabQty - 1,
      discountPercent: 0,
      pricePaise: basePricePaise,
      savingPerUnitPaise: 0,
    });
  }

  // Tier 1 Slab (e.g. 5+ bags)
  if (t1Qty > 0 && t1Pct > 0) {
    const t1Price = Math.round(basePricePaise * (1 - t1Pct / 100));
    tiers.push({
      tierName: `Volume Slab (${t1Qty}+)`,
      minQty: t1Qty,
      maxQty: (t2Qty > t1Qty && t2Pct > 0) ? t2Qty - 1 : undefined,
      discountPercent: t1Pct,
      pricePaise: t1Price,
      savingPerUnitPaise: basePricePaise - t1Price,
    });
  }

  // Tier 2 Slab (e.g. 20+ bags)
  if (t2Qty > 0 && t2Pct > 0) {
    const t2Price = Math.round(basePricePaise * (1 - t2Pct / 100));
    tiers.push({
      tierName: `Bulk Pallet (${t2Qty}+)`,
      minQty: t2Qty,
      discountPercent: t2Pct,
      pricePaise: t2Price,
      savingPerUnitPaise: basePricePaise - t2Price,
    });
  }

  return tiers;
}

export function calculateEffectiveTierPrice(item: QpsConfig | number, qty: number): {
  effectiveUnitPricePaise: number;
  discountPercent: number;
  totalPaise: number;
  savingsTotalPaise: number;
  appliedTier: VolumeTier | null;
  hasLadder: boolean;
} {
  const basePricePaise = typeof item === 'number' ? item : (item.pricePaise || 0);
  const tiers = getVolumeTiers(item);
  const qtyNormalized = Math.max(1, qty);

  if (tiers.length === 0) {
    return {
      effectiveUnitPricePaise: basePricePaise,
      discountPercent: 0,
      totalPaise: basePricePaise * qtyNormalized,
      savingsTotalPaise: 0,
      appliedTier: null,
      hasLadder: false,
    };
  }

  let activeTier = tiers[0];
  for (const t of tiers) {
    if (qtyNormalized >= t.minQty && (t.maxQty === undefined || qtyNormalized <= t.maxQty)) {
      activeTier = t;
      break;
    }
  }

  const effectiveUnitPricePaise = activeTier.pricePaise;
  const totalPaise = effectiveUnitPricePaise * qtyNormalized;
  const standardTotal = basePricePaise * qtyNormalized;
  const savingsTotalPaise = Math.max(0, standardTotal - totalPaise);

  return {
    effectiveUnitPricePaise,
    discountPercent: activeTier.discountPercent,
    totalPaise,
    savingsTotalPaise,
    appliedTier: activeTier,
    hasLadder: true,
  };
}
